import {
    action,
    computed,
    makeObservable,
    observable,
    runInAction,
} from "mobx";
import { BaseStore } from "stores/BaseStore";
import type { IRootStore } from "stores/RootStore";
import { SignalReportDatum } from "models/Reporting/ReportDatum";
import { SignalsService } from "services/SignalsService";
import SignalsEndpointCacheStore from
    "components/Signals/Store/SignalsEndpointCacheStore";
import type {
    SignalDataEndpoint,
    SignalsRequest,
} from "components/Signals/SignalsModels";
import type { ZoomPathEntry } from "../../Drilldown/DrilldownTypes";

/**
 * Per-chart-card sub-store that owns zoom state for a zoomable chart. Mirrors
 * the AISummaryStore pattern: instantiated by ChartCardStore in its constructor
 * (unconditionally — no capability gate; whether anything ever does zoom is
 * determined by whether response rows carry the `hasChildren` marker and by
 * the point menu's `viewLevel` visibility check). ChartCardStore knows when to
 * reset zoom and which dataset to display, but the path itself, the per-level
 * data, the fetch mechanism, and the cache reads/writes all live here.
 *
 * Caching: top-level chart data caches under `endpoint = dataEndpoint` via the
 * standard BaseObjectStore plumbing. Zoom data lives in its own LRU bucket
 * under `endpoint = ${dataEndpoint}/zoom`, with a key composed from the same
 * filters+selections+extras envelope plus the current zoomPath. Because the
 * bucket is keyed by a different endpoint string, deep-zoom navigation can
 * never evict the top-level entry.
 */
export interface ZoomStoreHost {
    readonly dataEndpoint: SignalDataEndpoint | undefined;
    readonly baseObjectId: string;
    // Pulls the chart's filters+selections envelope at call time and appends
    // `zoomPath`. Implemented on ChartCardStore as a thin wrapper around its
    // private getChartState().
    buildZoomRequestBody(
        zoomPath: string[],
    ): SignalsRequest & { zoomPath: string[] };
    // Same cache-key parts the base ChartCardStore uses for its top-level
    // entry. ZoomStore mixes these with `zoomPath` to build its own key.
    getZoomCacheKeyEnvelope(): unknown;
    // Provides the chart's current abort signal so an in-flight zoom request
    // is cancelled when the chart's overall fetch is cancelled (e.g. a
    // filter change that triggers runRefresh, which also resets the zoom).
    getAbortSignal(): AbortSignal;
}

type ZoomCacheEntry = {
    data: SignalReportDatum[];
    sqlQuery?: string;
};

class ZoomStore extends BaseStore {
    private readonly signalsService: SignalsService = new SignalsService();
    private readonly cacheStore: SignalsEndpointCacheStore;
    private readonly host: ZoomStoreHost;

    @observable zoomPath: ZoomPathEntry[] = [];
    // Snapshot of zoomPath at the moment the *currently rendered* data was
    // fetched. Lags behind `zoomPath` during an in-flight zoom so callers can
    // derive per-level visuals (color palette, header template) from the
    // level that actually matches the cells on screen — otherwise the chrome
    // updates eagerly while the data stays on the previous level for ~350ms.
    // The breadcrumb still binds to `zoomPath` so the user sees the path
    // change immediately on click.
    @observable displayedZoomPath: ZoomPathEntry[] = [];
    @observable isFetching: boolean = false;
    @observable currentLevelData: SignalReportDatum[] | undefined = undefined;
    @observable currentLevelSqlQuery: string | undefined = undefined;
    @observable error: string | null = null;

    // Tracks the most recent zoom request so out-of-order responses can be
    // discarded. Together with the response's echoed `zoomPath`, this is the
    // rapid-click guard described in the design doc.
    private inFlightRequestId: number = 0;

    constructor({
        host,
        rootStore,
    }: {
        host: ZoomStoreHost;
        rootStore: IRootStore;
    }) {
        super("Zoom Store");
        makeObservable(this);
        this.host = host;
        this.cacheStore = rootStore.getStore(SignalsEndpointCacheStore);
    }

    @computed get isZoomed(): boolean {
        return this.zoomPath.length > 0;
    }

    @computed get currentDepth(): number {
        return this.zoomPath.length;
    }

    // The zoom-path identifier list the backend sees. Falls back to `x` when
    // a row has no stable identifier.
    @computed get zoomPathIdentifiers(): string[] {
        return this.zoomPath.map((entry) => entry.xIdentifier ?? entry.x);
    }

    @action
    async zoomInto(entry: Omit<ZoomPathEntry, "depth">): Promise<void> {
        const nextEntry: ZoomPathEntry = {
            ...entry,
            depth: this.zoomPath.length,
        };
        await this.setZoomPath([...this.zoomPath, nextEntry]);
    }

    @action
    async zoomOut(): Promise<void> {
        await this.setZoomPath(this.zoomPath.slice(0, -1));
    }

    @action
    async navigateToDepth(depth: number): Promise<void> {
        // depth = 0 clears the stack entirely; the chart card's displayedData
        // computed falls back to the base level data when not zoomed.
        const clamped = Math.max(0, Math.min(depth, this.zoomPath.length));
        await this.setZoomPath(this.zoomPath.slice(0, clamped));
    }

    /**
     * Synchronously clears all zoom state. Called by ChartCardStore inside
     * runRefresh before the new fetch is scheduled — this is the single broad
     * rule that subsumes "filter change auto-pops" and "Refresh resets" and
     * eliminates ordering races with in-flight zoom fetches (the path is gone
     * before the next fetch starts).
     */
    @action
    reset(): void {
        this.zoomPath = [];
        this.displayedZoomPath = [];
        this.currentLevelData = undefined;
        this.currentLevelSqlQuery = undefined;
        this.error = null;
        // Bump the in-flight id so any pending response is rejected on arrival.
        this.inFlightRequestId++;
        this.isFetching = false;
    }

    @action
    private async setZoomPath(nextPath: ZoomPathEntry[]): Promise<void> {
        this.zoomPath = nextPath;
        if (nextPath.length === 0) {
            // Returning to the top level: the chart now displays the base
            // data, so the displayed path catches up immediately.
            this.displayedZoomPath = [];
            this.currentLevelData = undefined;
            this.currentLevelSqlQuery = undefined;
            this.inFlightRequestId++;
            this.isFetching = false;
            return;
        }
        await this.fetchCurrentLevel();
    }

    private buildCacheKey(zoomPath: string[]): string {
        // Mix the same envelope ChartCardStore uses (filters + selections +
        // extras) with zoomPath so two different filter contexts at the same
        // path land in different cache slots.
        return JSON.stringify({
            envelope: this.host.getZoomCacheKeyEnvelope() ?? null,
            zoomPath,
        });
    }

    private get zoomEndpointKey(): string {
        const endpoint = this.host.dataEndpoint;
        return `${endpoint ?? ""}/zoom`;
    }

    @action
    private async fetchCurrentLevel(): Promise<void> {
        const endpoint = this.host.dataEndpoint;
        if (!endpoint) return;

        const requestId = ++this.inFlightRequestId;
        const pathIdentifiers = this.zoomPathIdentifiers;
        const cacheKey = this.buildCacheKey(pathIdentifiers);
        this.isFetching = true;
        this.error = null;

        // Cache hit: serve instantly, no network. Each cache key is unique to
        // this (filters, selections, zoomPath) combination.
        const cached = this.cacheStore.get<ZoomCacheEntry>({
            endpoint: this.zoomEndpointKey,
            key: cacheKey,
            scope: "primaryData",
        });
        if (cached) {
            if (requestId !== this.inFlightRequestId) return;
            runInAction(() => {
                this.currentLevelData = cached.data;
                this.currentLevelSqlQuery = cached.sqlQuery;
                this.displayedZoomPath = [...this.zoomPath];
                this.isFetching = false;
            });
            return;
        }

        try {
            const body = this.host.buildZoomRequestBody(pathIdentifiers);
            const response = await this.signalsService.getChartZoom(
                endpoint,
                body,
                this.host.getAbortSignal(),
            );

            if (requestId !== this.inFlightRequestId) return;

            // Path-echo guard: discard the response if its echoed zoomPath no
            // longer matches what the user is currently looking at (rapid
            // double-click into two different cells).
            const echoed = response.zoomPath ?? [];
            const currentIds = this.zoomPathIdentifiers;
            if (
                echoed.length !== currentIds.length ||
                echoed.some((v, i) => v !== currentIds[i])
            ) {
                runInAction(() => {
                    this.isFetching = false;
                    this.error = "Zoom response did not match the requested path.";
                });
                return;
            }

            runInAction(() => {
                this.currentLevelData = response.data;
                this.currentLevelSqlQuery = response.sqlQuery;
                this.displayedZoomPath = [...this.zoomPath];
                this.isFetching = false;
            });

            this.cacheStore.set({
                endpoint: this.zoomEndpointKey,
                key: cacheKey,
                scope: "primaryData",
                value: {
                    data: response.data,
                    sqlQuery: response.sqlQuery,
                },
            });
        } catch (e: unknown) {
            if (requestId !== this.inFlightRequestId) return;
            const aborted =
                (e as { name?: string })?.name === "AbortError" ||
                (e as { name?: string })?.name === "CanceledError";
            runInAction(() => {
                this.isFetching = false;
                if (!aborted) {
                    this.error =
                        (e as Error)?.message ?? "Zoom fetch failed";
                }
            });
        }
    }
}

export default ZoomStore;
